/**
 * Notification subsystem service — mirror of the
 * `/api/v1/workspace/notification*` rows in HTTP_API.md.
 *
 * Source: backend/app/api/v1/notifications.py.
 */

import { api } from "./api";
import type {
  NotificationFeedResponse,
  NotificationPreferencesResponse,
  NotificationPreferencesUpdateRequest,
  NotificationsClearAllResponse,
} from "@/types";

/** GET /api/v1/workspace/notifications — the mute-filtered bell feed. */
export async function fetchNotifications(
  params: { limit?: number } = {},
): Promise<NotificationFeedResponse> {
  const { data } = await api.get<NotificationFeedResponse>(
    "/api/v1/workspace/notifications",
    { params: { limit: params.limit } },
  );
  return data;
}

/** GET /api/v1/workspace/notification-preferences — per-event mute state. */
export async function fetchNotificationPreferences(): Promise<NotificationPreferencesResponse> {
  const { data } = await api.get<NotificationPreferencesResponse>(
    "/api/v1/workspace/notification-preferences",
  );
  return data;
}

/**
 * PUT /api/v1/workspace/notification-preferences — replace the user's
 * muted-event-type set (idempotent). A 422 `unknown_event_type` from the
 * backend propagates so the caller can revert + surface the rejection.
 */
export async function putNotificationPreferences(
  payload: NotificationPreferencesUpdateRequest,
): Promise<NotificationPreferencesResponse> {
  const { data } = await api.put<NotificationPreferencesResponse>(
    "/api/v1/workspace/notification-preferences",
    payload,
  );
  return data;
}

/**
 * POST /api/v1/workspace/notifications/clear-all — dismiss every
 * currently-shown notification (experience-and-ui.md § 3a.2; sub-B3).
 *
 * The derived feed is filtered server-side by a single per-user
 * `notifications_cleared_at` timestamp, so a cleared notification stays cleared
 * across the bell + dashboard + reloads. No request body; idempotent.
 */
export async function clearAllNotifications(): Promise<NotificationsClearAllResponse> {
  const { data } = await api.post<NotificationsClearAllResponse>(
    "/api/v1/workspace/notifications/clear-all",
  );
  return data;
}
